import { useCallback } from 'react'
import { useTranslation } from 'next-i18next'
import styled from '@emotion/styled'
import useFlow from '../../../state/use-flow'
import useOrder from 'modules/order/state/order/use-order'
import useConfig from 'modules/config/state/use-config'
import Step from '../../step/step'
import ContactQuestionsForm from '../single/components/contact-questions-form/contact-questions-form'
import CustomQuestions from '../single/components/custom-questions/custom-questions'
import { validateForms, saveFormsData } from '../single/utils/single-step-form-orchestrator'
import DonorStepHeader from './components/donor-step-header'
import DonorInfoSummaryCard from './components/donor-info-summary-card'
import FormSectionDivider from './components/form-section-divider'
import Comment from '../single/components/comment/comment'
import { parseRawOrderCustomAnswers } from 'modules/order/state/custom-answers/custom-answers-utils'

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const PostConversionQuestionsWrapper = styled.div`
  label {
    font-weight: bold !important;
    color: black;
  }
`

export default function DonorStep() {
  const { t } = useTranslation()
  const flow = useFlow()
  const order = useOrder()
  const config = useConfig()

  // Check for optional questions
  const hasContactQuestions = config.questions.contact.some(
    (question) => question.is_required === false && question.tag,
  )

  const hasCustomQuestions = config.questions.custom.some(
    (question) => question.is_required === false,
  )

  // Show divider only when both sections have content
  const shouldShowDivider = hasContactQuestions && hasCustomQuestions

  const handleOnClick = () => {
    flow.goNextPostConversion()
  }

  const onSubmit = useCallback(async () => {
    try {
      const validationResult = await validateForms()

      if (validationResult.success) {
        // Save form data to Redux store
        saveFormsData(validationResult.results, order)

        // Extract form data from validation results
        const contactQuestionsResult = validationResult.results.find(
          (result) => result.formName === 'optionalContactQuestions',
        )

        const customQuestionsResult = validationResult.results.find(
          (result) => result.formName === 'optionalCustomQuestions',
        )

        // Merge current Redux data with fresh form data
        const mergedContactData = {
          ...order.contactAnswers.value,
          ...(contactQuestionsResult?.success ? contactQuestionsResult.data : {}),
        }

        const mergedCustomData = {
          ...order.customAnswers.value,
          ...(customQuestionsResult?.success ? customQuestionsResult.data : {}),
        }

        const parsedCustomData = parseRawOrderCustomAnswers(config, mergedCustomData)

        order.contactAnswers.save({
          contactAnswers: mergedContactData,
          customAnswers: parsedCustomData,
        })

        if (order.comment.value.trim()) {
          order.comment.save(order.comment.value)
        }

        flow.goNextPostConversion()
      }
    } catch (error) {
      // Form validation handles error display
    }
  }, [order, config, flow])

  return (
    <Step.Root header={{ headline: '' }}>
      <Step.Content>
        <DonorStepHeader />
        <DonorInfoSummaryCard />

        <FormContainer>
          {hasContactQuestions && <ContactQuestionsForm showRequired={false} />}
          {shouldShowDivider && <FormSectionDivider margin="0" />}
          {hasCustomQuestions && (
            <PostConversionQuestionsWrapper>
              <CustomQuestions showRequired={false} />
            </PostConversionQuestionsWrapper>
          )}
          {config.comment.isEnabled && <Comment />}

          <Step.Buttons>
            <Step.Button
              variant="skip"
              type="button"
              onClick={handleOnClick}
              dataId="donor-step-skip-button"
            >
              {t('DonorStep.skipButton', 'Skip')}
            </Step.Button>
            <Step.Button
              variant="submit"
              type="button"
              onClick={onSubmit}
              dataId="donor-step-submit-button"
            >
              {t('DonorStep.submitButton', 'Submit')}
            </Step.Button>
          </Step.Buttons>
        </FormContainer>
      </Step.Content>
    </Step.Root>
  )
}
